import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Cpu, Layers, Handshake } from "lucide-react";
import { WhatsAppCTA } from "@/components/WhatsApp";

const CAPACITY = [
  { icon: Cpu, title: "Usinagem CNC", desc: "Centros de usinagem, tornos e fresadoras para peças de precisão." },
  { icon: Layers, title: "Corte e fabricação", desc: "Corte plasma, solda e fabricação de peças e estruturas sob desenho." },
  { icon: TrendingUp, title: "Projetos especiais", desc: "Soluções desenvolvidas de acordo com a necessidade de cada cliente." },
  { icon: Handshake, title: "Atendimento próximo", desc: "Análise de cada desenho com sugestões de otimização de fabricação." },
];

export default function Sobre() {
  return (
    <div className="pt-[72px]" data-testid="sobre-page">
      <section className="py-20 border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-orange-500" />
            <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">quem somos</span>
          </div>
          <h1 className="display text-4xl sm:text-6xl font-extrabold uppercase">Sobre a Alpha Metais</h1>
        </div>
      </section>

      {/* 1. Sobre a Alpha Metais */}
      <section className="py-16" data-testid="sobre-historia">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="display text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
              Soluções em metais, <span className="text-orange-500">em constante evolução</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed text-sm sm:text-base">
              A Alpha Metais é uma empresa jovem, criada com o propósito de oferecer soluções em
              fabricação, usinagem e transformação de metais, sempre buscando evolução e aprimoramento.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base">
              Mesmo com uma história recente, a empresa vem passando por um processo constante de
              desenvolvimento, investindo em máquinas, tecnologia, estrutura e capacitação para
              ampliar sua capacidade produtiva.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base">
              Nossa estrutura reúne diferentes equipamentos e processos, permitindo atender demandas
              de diversos setores do mercado — desde a fabricação de peças e componentes até projetos
              especiais desenvolvidos de acordo com a necessidade de cada cliente.
            </p>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1608126841830-53832c4b326f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxDTkMlMjBtYWNoaW5pbmclMjBtZXRhbCUyMHdvcmtzaG9wfGVufDB8fHx8MTc4OTM1NDAwOXww&ixlib=rb-4.1.0&q=85"
              alt="Operador trabalhando na oficina Alpha Metais"
              className="rounded-sm border border-zinc-800 w-full object-cover h-[300px] sm:h-[420px]"
              data-testid="sobre-photo"
            />
          </div>
        </div>
      </section>

      {/* 2. Nossa trajetória */}
      <section className="py-16 border-t border-zinc-800 bg-zinc-900/40" data-testid="sobre-trajetoria">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-10 bg-orange-500" />
              <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">nossa trajetória</span>
            </div>
            <h2 className="display text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
              Jovem, e em <span className="text-orange-500">constante movimento</span>
            </h2>
          </div>
          <p className="text-zinc-400 leading-relaxed text-sm sm:text-base">
            Acreditamos que uma empresa precisa estar em constante movimento. Por isso, buscamos
            continuamente novas tecnologias, melhorias nos processos e expansão da nossa capacidade
            produtiva — cada projeto é uma oportunidade de evoluir estrutura, técnica e atendimento.
          </p>
        </div>
      </section>

      {/* 3. Estrutura e tecnologia */}
      <section className="py-16 border-t border-zinc-800" data-testid="sobre-estrutura">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-orange-500" />
            <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">estrutura e tecnologia</span>
          </div>
          <h2 className="display text-3xl sm:text-4xl font-extrabold uppercase leading-tight max-w-3xl">
            Estrutura para <span className="text-orange-500">diferentes desafios</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed text-sm sm:text-base max-w-3xl">
            Contamos com uma estrutura diversificada de máquinas e processos produtivos, permitindo
            trabalhar com diferentes materiais, dimensões e necessidades de fabricação.
          </p>
          <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base max-w-3xl">
            Nossa capacidade envolve usinagem CNC, torneamento, corte plasma, fabricação, projetos
            especiais e outros processos que podem ser combinados de acordo com a necessidade de cada projeto.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPACITY.map((c) => (
              <div key={c.title} className="card-tactical rounded-md p-6" data-testid={`sobre-cap-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <c.icon className="w-7 h-7 text-orange-500" strokeWidth={1.5} />
                <h3 className="display mt-3 text-lg font-bold uppercase tracking-wide">{c.title}</h3>
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Capacidade para diferentes setores */}
      <section className="py-16 border-t border-zinc-800 bg-zinc-900/40" data-testid="sobre-setores">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <h2 className="display text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
            Capacidade para <span className="text-orange-500">diferentes setores</span>
          </h2>
          <p className="text-zinc-400 leading-relaxed text-sm sm:text-base">
            Essa diversidade permite que a Alpha Metais atenda diferentes segmentos do mercado e
            desenvolva soluções específicas para cada cliente — de demandas pontuais, como peças
            unitárias e reposição, a projetos mais complexos e lotes seriados.
          </p>
        </div>
      </section>

      {/* 5. Compromisso com o cliente */}
      <section className="py-20 border-t border-zinc-800" data-testid="sobre-compromisso">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="display text-3xl sm:text-4xl font-extrabold uppercase leading-tight">
            Compromisso com <span className="text-orange-500">o cliente</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed text-sm sm:text-base max-w-2xl mx-auto">
            Nosso objetivo é construir relações de longo prazo com nossos clientes, oferecendo
            soluções com qualidade, precisão, agilidade e comprometimento.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/painel/orcamentos/novo"
              data-testid="sobre-quote-button"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-semibold uppercase tracking-wider px-7 py-3.5 rounded-sm transition-colors"
            >
              Solicitar orçamento <ArrowRight className="w-4 h-4" />
            </Link>
            <WhatsAppCTA message="Olá! Gostaria de conhecer melhor a Alpha Metais." testId="sobre-whatsapp-cta" />
          </div>
        </div>
      </section>
    </div>
  );
}
